import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
// import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Clock, Loader2, Copy, ThumbsUp, ThumbsDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useWorkflow } from '@/hooks/useWorkflow';


// Types
interface WorkflowStageProps {
  stage: string;
  currentStage: string;
  status: string;
  title: string;
  icon: LucideIcon;
}

// Helper function
const getStageOrder = (stage: string): number => {
  const stages = ['idle', 'initializing', 'generating_idea', 'creating_content', 'reviewing', 'final'];
  return stages.indexOf(stage);
};

// Stage component for visual workflow representation
const WorkflowStage: React.FC<WorkflowStageProps> = ({ stage, currentStage, status, title, icon: Icon }) => {
  const isActive = currentStage === stage;
  const isCompleted = getStageOrder(currentStage) > getStageOrder(stage) && status !== 'error';
  const isError = currentStage === stage && status === 'error';

  return (
    <div className={`flex flex-col items-center p-4 rounded-lg border transition-all duration-300 ${isActive ? 'border-blue-500 bg-blue-50' :
      isCompleted ? 'border-green-500 bg-green-50' :
        isError ? 'border-red-500 bg-red-50' :
          'border-gray-200 bg-gray-50'
      }`}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${isActive ? 'bg-blue-500 text-white' :
        isCompleted ? 'bg-green-500 text-white' :
          isError ? 'bg-red-500 text-white' :
            'bg-gray-300 text-gray-600'
        }`}>
        {isActive && status === 'running' ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : isCompleted ? (
          <CheckCircle className="w-6 h-6" />
        ) : isError ? (
          <XCircle className="w-6 h-6" />
        ) : (
          <Icon className="w-6 h-6" />
        )}
      </div>
      <span className={`text-sm font-medium ${isActive ? 'text-blue-700' :
        isCompleted ? 'text-green-700' :
          isError ? 'text-red-700' :
            'text-gray-600'
        }`}>
        {title}
      </span>
    </div>
  );
};




export const WorkflowVisualization: React.FC = () => {
  const { workflowState } = useWorkflow();

  if (!workflowState.isActive) return null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Content Creation Progress</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <WorkflowStage
            stage="generating_idea"
            currentStage={workflowState.currentStage}
            status={workflowState.status}
            title="Generate Idea"
            icon={Clock}
          />
          <WorkflowStage
            stage="creating_content"
            currentStage={workflowState.currentStage}
            status={workflowState.status}
            title="Create Content"
            icon={Clock}
          />
          <WorkflowStage
            stage="reviewing"
            currentStage={workflowState.currentStage}
            status={workflowState.status}
            title="Review Content"
            icon={Clock}
          />
          <WorkflowStage
            stage="final"
            currentStage={workflowState.currentStage}
            status={workflowState.status}
            title="Final Review"
            icon={CheckCircle}
          />
        </div>
        {/* 
        {workflowState.progress > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span>Progress</span>
              <span>{Math.round(workflowState.progress)}%</span>
            </div>
            <Progress value={workflowState.progress} className="h-2" />
          </div>
        )} */}

        {/* {workflowState.preview && (
          <Alert className="mb-4">
            <AlertDescription>
              <strong>Current Status:</strong> {workflowState.preview}
            </AlertDescription>
          </Alert>
        )} */}

        {workflowState.error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{workflowState.error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export const ContentReview: React.FC = () => {
  const { workflowState, provideFeedback, approveContent, resetWorkflow, fetchCurrentContent } = useWorkflow();
  const [feedback, setFeedback] = useState<string>('');
  const [showFeedbackForm, setShowFeedbackForm] = useState<boolean>(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState<boolean>(false);
  interface ContentData {
    title: string;
    content: string;
    review_score: number;
    review_feedback?: string;
  }
  const [currentContent, setCurrentContent] = useState<ContentData | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false);

  // Fetch content when workflow reaches final stage
  useEffect(() => {
    const loadContent = async () => {
      if (workflowState.currentStage === 'final' &&
        workflowState.status === 'completed' &&
        workflowState.threadId &&
        !currentContent) {

        setIsLoadingContent(true);
        try {
          const content = await fetchCurrentContent();
          setCurrentContent(content as ContentData);
          console.log('Content loaded for review:', content);
        } catch (error) {
          console.error('Failed to load content:', error);
        } finally {
          setIsLoadingContent(false);
        }
      }
    };

    loadContent();
  }, [workflowState.currentStage, workflowState.status, workflowState.threadId, currentContent, fetchCurrentContent]);

  // Clear content when workflow resets or feedback is submitted
  useEffect(() => {
    if (!workflowState.isActive || workflowState.currentStage !== 'final') {
      setCurrentContent(null);
      setShowFeedbackForm(false);
    }
  }, [workflowState.isActive, workflowState.currentStage]);

  // Only show when workflow is at final stage
  if (!workflowState.isActive || workflowState.currentStage !== 'final' || workflowState.status === 'running') {
    return null;
  }

  const handleApprove = async (): Promise<void> => {
    try {
      await approveContent();
    } catch (error) {
      console.error('Error approving content:', error);
    }
  };

  const handleDisapprove = (): void => {
    setShowFeedbackForm(true);
  };

  const handleFeedback = async (): Promise<void> => {
    if (!feedback.trim()) return;

    setIsSubmittingFeedback(true);
    try {
      await provideFeedback(feedback);
      setFeedback('');
      setShowFeedbackForm(false);
      setCurrentContent(null); // Clear content to trigger refetch
    } catch (error) {
      console.error('Feedback error:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>How does this look sir?</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoadingContent ? (
          <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-center">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            <span>Loading content for review...</span>
          </div>
        ) : currentContent ? (
          <div className="space-y-4">
            {/* <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-bold text-lg text-blue-900 mb-2">{currentContent.title}</h3>
              <div className="text-sm text-blue-700">
                <strong>AI Review Score:</strong> {currentContent.review_score.toFixed(1)}/1.0
              </div> 
            </div> */}

            {/* Full Content Display */}
            <div className="p-4 bg-white rounded-lg border border-gray-300 max-h-96 overflow-y-auto">
              <h4 className="font-semibold mb-3 text-gray-800">{currentContent.title}</h4>
              <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed font-medium">
                {currentContent.content}
              </div>
            </div>
            {/* 
            {currentContent.review_feedback && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">AI Review Notes:</h4>
                <p className="text-sm text-yellow-700">{currentContent.review_feedback}</p>
              </div>
            )} */}
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-2">Content Ready for Review</h3>
            <div className="text-sm text-gray-700">
              <p className="mb-2"><strong>Status:</strong> {workflowState.preview || "Content generation completed!"}</p>
              <p className="text-xs text-gray-500">Loading full content...</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!showFeedbackForm ? (
          <div className="flex flex-col space-y-3 md:flex-row md:space-x-3">
            <Button
              onClick={handleApprove}
              className="flex-1"
              size="lg"
              disabled={!currentContent || isLoadingContent}
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              Approve & Post
            </Button>
            <Button
              onClick={handleDisapprove}
              variant="outline"
              className="flex-1"
              size="lg"
              disabled={!currentContent || isLoadingContent}
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Disapprove
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Provide Feedback:</strong> Tell me what you'd like to change about the content.
              </p>
            </div>
            <Textarea
              placeholder="What would you like to change about the content? Be specific about tone, style, or any particular aspects..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
            />
            <div className="flex flex-col space-y-3 md:flex-row md:space-x-3">
              <Button
                onClick={handleFeedback}
                disabled={!feedback.trim() || isSubmittingFeedback}
                className="flex-1"
              >
                {isSubmittingFeedback ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Submit Feedback & Regenerate
              </Button>
              <Button
                onClick={() => {
                  setShowFeedbackForm(false);
                  setFeedback('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="pt-4 border-t">
          <Button
            onClick={resetWorkflow}
            variant="outline"
            size="sm"
            className="w-full"
          >
            Start New Workflow
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const CompletionMessage: React.FC = () => {
  const { workflowState, resetWorkflow } = useWorkflow();

  if (workflowState.status !== 'approved' || !workflowState.finalContent) {
    return null;
  }

  return (
    <Card className="mb-6 border-green-200 bg-green-50">
      <CardHeader>
        <CardTitle className="text-green-800 flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          Content Approved & Copied!
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Alert>
            <Copy className="h-4 w-4" />
            <AlertDescription>
              Your content has been copied to the clipboard. You can now paste it wherever you need it!
            </AlertDescription>
          </Alert>

          <div className="p-4 bg-white rounded-lg border">
            <h4 className="font-semibold mb-2">{workflowState.finalContent.title}</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {workflowState.finalContent.content}
            </p>
          </div>

          <div className="text-sm text-gray-600">
            <strong>Review Score:</strong> {workflowState.finalContent.review_score.toFixed(1)}/1.0
          </div>

          <Button onClick={resetWorkflow} className="w-full">
            Create New Content
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};